import { useCallback, useMemo, useState } from "react";
import { Pressable, Text, useWindowDimensions, View } from "react-native";
import {
  ArrowDownUp,
  FileText,
  History,
  Minus,
  Plus,
  RefreshCw,
  Syringe,
  type LucideIcon,
} from "lucide-react-native";
import {
  CardSurface,
  CompactIconTabs,
  Dialog,
  DialogHeader,
  IconButton,
  PrimaryButton,
  KebabMenu,
  SectionTitle,
  StatusBadge,
  TextField,
} from "@louise/moon-ui";
import { EMBER } from "../../../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";
import { DocumentRow } from "../../documents/DocumentRow";
import MedRow, { MedFormIcon } from "../rows/MedRow";
import type { MedicationScheduleItem, MedForm } from "../schedule.types";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDate } from "@src/packages/i18n/formatters";
import { useCreatePatientNote } from "@src/packages/care/hooks/usePatientNotes";
import { getTodayString } from "@src/packages/utils/date";
import { useTimezone } from "@src/packages/preferences";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type ProtocolTabKey = "medications" | "timeline";

export type CycleProtocolNote = {
  id?: string;
  date: string;
  dayLabel?: string | null;
  text: string;
};

export type CycleProtocolMedicationPeriod = {
  id: string;
  name: string;
  doseLabel?: string | null;
  form: MedForm;
  route?: string | null;
  startDate: string;
  endDate?: string | null;
  timeTaken?: string[] | null;
  notes?: string | null;
  stopped?: boolean;
};

export type CycleProtocolDoseLog = {
  medicationId: string;
  scheduledDate: string;
  scheduledTime: string;
  status: "taken" | "late" | "missed";
};

export type CycleProtocolMedication = MedicationScheduleItem & {
  dosageBadges?: string[];
  periods?: CycleProtocolMedicationPeriod[];
};

export type CycleProtocolData = {
  id?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  medications?: (string | CycleProtocolMedication)[] | null;
  medicationPeriods?: CycleProtocolMedicationPeriod[] | null;
  doseLogs?: CycleProtocolDoseLog[] | null;
  notes?: CycleProtocolNote[] | null;
};

type TimelineEventKind = "added" | "dose_changed" | "swapped" | "stopped";

type TimelineEventTag = {
  kind: TimelineEventKind;
  label: string;
  icon: LucideIcon;
};

const TIMELINE_EVENT_ICONS: Record<TimelineEventKind, LucideIcon> = {
  added: Plus,
  dose_changed: ArrowDownUp,
  swapped: RefreshCw,
  stopped: Minus,
};

function getProtocolTabs(t: TranslationFunction): {
  value: ProtocolTabKey;
  label: string;
  icon: LucideIcon;
}[] {
  return [
    {
      value: "medications",
      label: t("care.cycle_protocol.tabs.medications"),
      icon: Syringe,
    },
    {
      value: "timeline",
      label: t("care.cycle_protocol.tabs.timeline"),
      icon: History,
    },
  ];
}

const protocolVialSyringeIcon =
  require("../../../../assets/images/vial-syringe.png") as number;

const TEXT_LIGHT = semanticColors.text.tertiary;
const TEXT_MID = semanticColors.text.secondary;

function hasText(value: string | null | undefined) {
  return typeof value === "string" && value.trim().length > 0;
}

function formatRouteLabel(route: string | null | undefined) {
  if (!route) return null;
  return route
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatMedicationSubtitle(
  medication: CycleProtocolMedication,
  t: TranslationFunction,
) {
  const route = formatRouteLabel(medication.route);
  const times = medication.userContext?.timeTaken?.length
    ? medication.userContext.timeTaken.join(", ")
    : medication.time;

  return [
    medication.recurrence === "daily"
      ? t("care.cycle_protocol.labels.daily")
      : t("care.cycle_protocol.labels.once"),
    route,
    times,
    medication.sinceDay && medication.untilDay
      ? `${medication.sinceDay}-${medication.untilDay}`
      : medication.sinceDay
        ? t("care.cycle_protocol.labels.since", {
            params: { day: medication.sinceDay },
          })
        : medication.untilDay
          ? t("care.cycle_protocol.labels.until", {
              params: { day: medication.untilDay },
            })
          : medication.periods?.length === 0
            ? t("documents.date_unknown")
            : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function parseLegacyNote(text: string) {
  try {
    const parsed = JSON.parse(text) as {
      __protocol_adjustment__?: boolean;
      title?: string;
      detail?: string;
      reason?: string;
    };
    if (!parsed.__protocol_adjustment__) return text;
    return [parsed.title, parsed.detail, parsed.reason]
      .filter((value) => hasText(value))
      .join(" · ");
  } catch {
    return text;
  }
}

function toDateKey(value: Date) {
  return value.toISOString().slice(0, 10);
}

function parseDate(value: string) {
  return new Date(`${value}T12:00:00`);
}

function addDays(value: string, amount: number) {
  const date = parseDate(value);
  date.setDate(date.getDate() + amount);
  return toDateKey(date);
}

function clampDate(value: string, min?: string | null, max?: string | null) {
  if (min && value < min) return min;
  if (max && value > max) return max;
  return value;
}

function medicationIdentity(period: CycleProtocolMedicationPeriod) {
  return `${period.name.trim().toLowerCase()}|${period.form}|${period.route ?? ""}`;
}

function getTimelineEventTags(
  date: string,
  periods: CycleProtocolMedicationPeriod[],
  t: TranslationFunction,
): TimelineEventTag[] {
  const tags = new Map<TimelineEventKind, TimelineEventTag>();
  const startsToday = periods.filter((period) => period.startDate === date);
  const startsSameIdentityToday = new Set(
    startsToday.map((period) => medicationIdentity(period)),
  );

  for (const period of startsToday) {
    const identity = medicationIdentity(period);
    const previousSameMedication = periods
      .filter(
        (candidate) =>
          medicationIdentity(candidate) === identity &&
          candidate.startDate < period.startDate &&
          (!candidate.endDate ||
            candidate.endDate >= addDays(period.startDate, -1)),
      )
      .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];

    if (
      previousSameMedication &&
      previousSameMedication.doseLabel !== period.doseLabel
    ) {
      tags.set("dose_changed", {
        kind: "dose_changed",
        label: t("care.cycle_protocol.timeline.event_dose_changed"),
        icon: TIMELINE_EVENT_ICONS.dose_changed,
      });
      continue;
    }

    const hasExplicitSwapNote = /swap|replac/i.test(period.notes ?? "");
    const previousMedicationEnded = periods.some(
      (candidate) =>
        medicationIdentity(candidate) !== identity &&
        candidate.endDate &&
        candidate.endDate >= addDays(date, -1) &&
        candidate.endDate <= date &&
        !startsSameIdentityToday.has(medicationIdentity(candidate)),
    );

    const kind =
      hasExplicitSwapNote || previousMedicationEnded ? "swapped" : "added";
    tags.set(kind, {
      kind,
      label: t(
        kind === "swapped"
          ? "care.cycle_protocol.timeline.event_swapped"
          : "care.cycle_protocol.timeline.event_added",
      ),
      icon: TIMELINE_EVENT_ICONS[kind],
    });
  }

  if (periods.some((period) => period.endDate === date && period.stopped)) {
    tags.set("stopped", {
      kind: "stopped",
      label: t("care.cycle_protocol.timeline.event_stopped"),
      icon: TIMELINE_EVENT_ICONS.stopped,
    });
  }

  return Array.from(tags.values());
}

function formatTime(value: string) {
  if (!value) return "—";
  const [hourValue, minute = "00"] = value.split(":");
  const hour = Number(hourValue);
  if (!Number.isFinite(hour)) return value;
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${suffix}`;
}

function periodDates(
  period: CycleProtocolMedicationPeriod,
  cycleStart: string | null | undefined,
  cycleEnd: string | null | undefined,
  today: string,
) {
  const start = clampDate(period.startDate, cycleStart, cycleEnd);
  const requestedEnd = period.endDate ?? today;
  const end = clampDate(requestedEnd, cycleStart, cycleEnd);
  if (end < start) return [];

  const dates: string[] = [];
  let cursor = start;
  while (cursor <= end) {
    dates.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return dates;
}

type MedicationOccurrence = {
  id: string;
  date: string;
  time: string;
  name: string;
  doseLabel: string;
  form: MedForm;
  route?: string | null;
  medicationId: string;
  status: "scheduled" | "upcoming" | "taken" | "late" | "missed";
  period: CycleProtocolMedicationPeriod;
};

function getStatusLabel(
  status: MedicationOccurrence["status"],
  t: TranslationFunction,
) {
  switch (status) {
    case "taken":
      return t("care.cycle_protocol.timeline.status_taken");
    case "late":
      return t("care.cycle_protocol.timeline.status_late");
    case "missed":
      return t("care.cycle_protocol.timeline.status_missed");
    case "upcoming":
      return t("care.cycle_protocol.timeline.status_upcoming");
    default:
      return t("care.cycle_protocol.timeline.status_scheduled");
  }
}

function getStatusTone(
  status: MedicationOccurrence["status"],
): "green" | "gold" | "coral" | "neutral" {
  switch (status) {
    case "taken":
      return "green";
    case "late":
      return "gold";
    case "missed":
      return "coral";
    default:
      return "neutral";
  }
}

function buildOccurrences(
  periods: CycleProtocolMedicationPeriod[],
  doseLogs: CycleProtocolDoseLog[],
  cycleStart: string | null | undefined,
  cycleEnd: string | null | undefined,
  today: string,
) {
  const logsByOccurrence = new Map(
    doseLogs.map((log) => [
      `${log.medicationId}|${log.scheduledDate}|${log.scheduledTime}`,
      log.status,
    ]),
  );
  const occurrences: MedicationOccurrence[] = [];

  for (const period of periods) {
    const times = period.timeTaken?.length ? period.timeTaken : [""];
    for (const date of periodDates(period, cycleStart, cycleEnd, today)) {
      for (const time of times) {
        const logged = logsByOccurrence.get(`${period.id}|${date}|${time}`);
        occurrences.push({
          id: `${period.id}|${date}|${time}`,
          date,
          time,
          name: period.name,
          doseLabel: period.doseLabel ?? "",
          form: period.form,
          route: period.route,
          medicationId: period.id,
          status: logged ?? (date > today ? "upcoming" : "scheduled"),
          period,
        });
      }
    }
  }

  return occurrences.sort((a, b) =>
    `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`),
  );
}

function TimelineMedicationRow({
  occurrence,
  onEdit,
  t,
}: {
  occurrence: MedicationOccurrence;
  onEdit?: (period: CycleProtocolMedicationPeriod) => void;
  t: TranslationFunction;
}) {
  const capturePress = usePressCapture("TimelineMedicationRow");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(occurrence.name), "timeline_medication_row");
        onEdit?.(occurrence.period);
      }}
      disabled={!onEdit}
      accessibilityRole={onEdit ? "button" : undefined}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingHorizontal: 0,
        paddingVertical: 10,
        backgroundColor: pressed
          ? semanticColors.overlay.pressed
          : "transparent",
      })}
    >
      <Text
        className="font-dm-sans"
        style={{ width: 58, fontSize: 12, color: TEXT_LIGHT }}
      >
        {formatTime(occurrence.time)}
      </Text>
      <MedFormIcon form={occurrence.form} size={26} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{ fontSize: 14, fontWeight: "600", color: EMBER.colors.text }}
        >
          {occurrence.name}
        </Text>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{ fontSize: 12, color: TEXT_LIGHT, marginTop: 2 }}
        >
          {[occurrence.doseLabel, formatRouteLabel(occurrence.route)]
            .filter(Boolean)
            .join(" · ") || t("care.cycle_protocol.timeline.no_dose")}
        </Text>
      </View>
      <StatusBadge
        label={getStatusLabel(occurrence.status, t)}
        tone={getStatusTone(occurrence.status)}
      />
    </Pressable>
  );
}

function AddTimelineNoteModal({
  visible,
  date,
  cycleId,
  onClose,
}: {
  visible: boolean;
  date: string;
  cycleId?: string | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const createNote = useCreatePatientNote();
  const [text, setText] = useState("");
  const { locale } = useLocale();

  const formattedDate = formatDate(parseDate(date), locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const close = () => {
    setText("");
    onClose();
  };

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    await createNote.mutateAsync({
      cycleId: cycleId ?? null,
      date,
      text: trimmed,
    });
    close();
  };

  return (
    <Dialog
      visible={visible}
      onClose={close}
      closeButton="minimal"
      closeAccessibilityLabel={t("learn.article_actions.close_accessibility")}
      closeAction="close_add_note"
      backdropPressLabel="dismiss_backdrop"
    >
      <DialogHeader
        title={t("care.cycle_protocol.timeline.add_note_title")}
        subtitle={formattedDate}
        style={{ marginBottom: 14 }}
      />
      <TextField
        notes
        autoFocus
        value={text}
        onChangeText={setText}
        placeholder={t("care.cycle_protocol.timeline.add_note_placeholder")}
        accessibilityLabel={t("care.cycle_protocol.timeline.add_note_title")}
        style={{ marginBottom: 16 }}
      />
      <PrimaryButton
        label={t("care.cycle_protocol.timeline.save_note")}
        onPress={save}
        loading={createNote.isPending}
        disabled={!text.trim()}
      />
    </Dialog>
  );
}

function EmptyProtocolRow({
  tab,
  onPress,
}: {
  tab: ProtocolTabKey;
  onPress: () => void;
}) {
  const t = useTranslations();
  return (
    <DocumentRow
      title={
        tab === "medications"
          ? t("care.cycle_protocol.empty.medications_title")
          : t("care.cycle_protocol.empty.timeline_title")
      }
      subtitle={
        tab === "medications"
          ? t("care.cycle_protocol.empty.medications_subtitle")
          : t("care.cycle_protocol.empty.timeline_subtitle")
      }
      imageSource={protocolVialSyringeIcon}
      trailing={
        <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
      }
      onPress={onPress}
      showChevron={false}
    />
  );
}

function CycleProtocolSectionTitle({
  activeTab,
  setActiveTab,
  compactTabs,
  onAddMedication,
}: {
  activeTab: ProtocolTabKey;
  setActiveTab: (tab: ProtocolTabKey) => void;
  compactTabs: boolean;
  onAddMedication: () => void;
}) {
  const t = useTranslations();

  return (
    <View
      style={{
        marginTop: 28,
        marginBottom: 10,
        marginLeft: 4,
        marginRight: 2,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <SectionTitle icon={Syringe}>
        {t("care.cycle_protocol.section_title")}
      </SectionTitle>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <CompactIconTabs
          options={getProtocolTabs(t)}
          value={activeTab}
          onChange={setActiveTab}
          compact={compactTabs}
        />
        <IconButton
          accessibilityLabel={t("care.cycle_protocol.add_medication")}
          action="care.cycle_protocol.add_medication"
          onPress={onAddMedication}
          size={compactTabs ? 32 : 36}
          surface="frosted"
        >
          <Plus size={compactTabs ? 14 : 15} color={EMBER.colors.textLight} />
        </IconButton>
      </View>
    </View>
  );
}

export default function CycleProtocolSection({
  cycle,
  onEditMedication,
  onAddMedication,
}: {
  cycle: CycleProtocolData;
  onEditMedication?: (
    med: MedicationScheduleItem,
    period?: CycleProtocolMedicationPeriod,
  ) => void;
  onAddMedication: () => void;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const timezone = useTimezone();
  const [activeTab, setActiveTab] = useState<ProtocolTabKey>("medications");
  const [noteDate, setNoteDate] = useState<string | null>(null);
  const { width } = useWindowDimensions();
  const compactTabs = width < 390;
  const today = getTodayString(timezone);

  // A row whose courses are all undated reads "Date unknown" and sorts last.
  const medications = (cycle.medications ?? [])
    .filter((med): med is CycleProtocolMedication => typeof med !== "string")
    .sort(
      (a, b) =>
        Number(a.periods?.length === 0) - Number(b.periods?.length === 0),
    );
  const occurrences = useMemo(
    () =>
      buildOccurrences(
        cycle.medicationPeriods ?? [],
        cycle.doseLogs ?? [],
        cycle.startDate,
        cycle.endDate,
        today,
      ),
    [
      cycle.doseLogs,
      cycle.endDate,
      cycle.medicationPeriods,
      cycle.startDate,
      today,
    ],
  );

  const notes = useMemo(
    () =>
      (cycle.notes ?? [])
        .map((note) => ({ ...note, text: parseLegacyNote(note.text) }))
        .filter((note) => hasText(note.text))
        .sort((a, b) => a.date.localeCompare(b.date)),
    [cycle.notes],
  );

  const timelineByDate = useMemo(() => {
    const dates = new Map<
      string,
      { occurrences: MedicationOccurrence[]; notes: CycleProtocolNote[] }
    >();
    for (const occurrence of occurrences) {
      const day = dates.get(occurrence.date) ?? { occurrences: [], notes: [] };
      day.occurrences.push(occurrence);
      dates.set(occurrence.date, day);
    }
    for (const note of notes) {
      const day = dates.get(note.date) ?? { occurrences: [], notes: [] };
      day.notes.push(note);
      dates.set(note.date, day);
    }
    return Array.from(dates.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [notes, occurrences]);

  const openNote = useCallback(
    (date?: string) => {
      const fallback = clampDate(today, cycle.startDate, cycle.endDate);
      setNoteDate(date ?? fallback);
    },
    [cycle.endDate, cycle.startDate, today],
  );

  const renderTimeline = () => {
    if (timelineByDate.length === 0) {
      return <EmptyProtocolRow tab="timeline" onPress={onAddMedication} />;
    }

    return (
      <CardSurface style={{ overflow: "hidden" }}>
        {timelineByDate.map(([date, day], index) => {
          const dayNumber = cycle.startDate
            ? Math.floor(
                (parseDate(date).getTime() -
                  parseDate(cycle.startDate).getTime()) /
                  (1000 * 60 * 60 * 24),
              ) + 1
            : null;
          const takenCount = day.occurrences.filter(
            (item) => item.status === "taken" || item.status === "late",
          ).length;
          const recap = day.occurrences.length
            ? `${day.occurrences.length} ${
                day.occurrences.length === 1
                  ? t("care.cycle_protocol.timeline.medication_singular")
                  : t("care.cycle_protocol.timeline.medication_plural")
              } ${t("care.cycle_protocol.timeline.scheduled_recap")}${
                takenCount > 0
                  ? ` · ${takenCount} ${t("care.cycle_protocol.timeline.logged_recap")}`
                  : ""
              }.`
            : t("care.cycle_protocol.timeline.note_only_recap");
          const eventTags = getTimelineEventTags(
            date,
            cycle.medicationPeriods ?? [],
            t,
          );

          return (
            <View
              key={date}
              style={{
                flexDirection: "row",
                paddingTop: 16,
                paddingBottom: 8,
                paddingHorizontal: 16,
              }}
            >
              <View
                style={{
                  width: 20,
                  alignItems: "center",
                  position: "relative",
                  marginRight: 10,
                }}
              >
                {index < timelineByDate.length - 1 ? (
                  <View
                    style={{
                      position: "absolute",
                      top: 8,
                      bottom: -16,
                      width: 2,
                      backgroundColor: semanticColors.accent.default,
                    }}
                  />
                ) : null}
                <View
                  style={{
                    zIndex: 1,
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    backgroundColor: EMBER.colors.surface,
                    borderWidth: 3,
                    borderColor: semanticColors.accent.default,
                  }}
                />
              </View>
              <View
                style={{
                  flex: 1,
                  minWidth: 0,
                  flexDirection: "column",
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 15,
                        fontWeight: "700",
                        color: TEXT_MID,
                      }}
                    >
                      {formatDate(parseDate(date), locale, {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })}
                    </Text>
                    {dayNumber && dayNumber > 0 ? (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          color: TEXT_LIGHT,
                          marginTop: 2,
                        }}
                      >
                        {t("care.cycle_protocol.timeline.day_label", {
                          params: { day: String(dayNumber) },
                        })}
                      </Text>
                    ) : null}
                  </View>
                  <KebabMenu
                    items={[
                      {
                        id: `add-note-${date}`,
                        label: t("care.cycle_protocol.timeline.add_note_menu"),
                        icon: FileText,
                        onPress: () => openNote(date),
                      },
                    ]}
                    accessibilityLabel={t(
                      "care.cycle_protocol.timeline.day_menu",
                      { params: { date } },
                    )}
                    buttonColor={TEXT_LIGHT}
                  />
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    marginTop: 8,
                    marginBottom: 4,
                    fontSize: 13,
                    color: TEXT_MID,
                    lineHeight: 19,
                  }}
                >
                  {recap}
                </Text>
                {eventTags.length > 0 ? (
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 6,
                    }}
                  >
                    {eventTags.map((tag) => (
                      <StatusBadge
                        key={tag.kind}
                        label={tag.label}
                        tone="neutral"
                        icon={tag.icon}
                      />
                    ))}
                  </View>
                ) : null}
                {day.occurrences.map((occurrence) => (
                  <TimelineMedicationRow
                    key={occurrence.id}
                    occurrence={occurrence}
                    onEdit={(period) => {
                      const medication = medications.find((item) =>
                        item.periods?.some(
                          (candidate) => candidate.id === period.id,
                        ),
                      );
                      if (medication) onEditMedication?.(medication, period);
                    }}
                    t={t}
                  />
                ))}
                {day.notes.map((note) => (
                  <View
                    key={note.id ?? `${note.date}-${note.text}`}
                    style={{
                      marginTop: 6,
                      marginBottom: 8,
                    }}
                  >
                    <View style={{ flexDirection: "row", gap: 8 }}>
                      <FileText size={15} color={TEXT_MID} strokeWidth={1.7} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          flex: 1,
                          fontSize: 13,
                          color: TEXT_MID,
                          lineHeight: 19,
                        }}
                      >
                        {note.text}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          );
        })}
      </CardSurface>
    );
  };

  return (
    <View>
      <CycleProtocolSectionTitle
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        compactTabs={compactTabs}
        onAddMedication={onAddMedication}
      />

      {activeTab === "medications" ? (
        medications.length === 0 ? (
          <EmptyProtocolRow tab="medications" onPress={onAddMedication} />
        ) : (
          <CardSurface style={{ overflow: "hidden" }}>
            {medications.map((medication) => (
              <MedRow
                key={medication.id}
                item={medication}
                subtitle={formatMedicationSubtitle(medication, t)}
                dosageBadges={medication.dosageBadges}
                onEditMedication={onEditMedication}
              />
            ))}
          </CardSurface>
        )
      ) : (
        renderTimeline()
      )}

      {noteDate ? (
        <AddTimelineNoteModal
          visible
          date={noteDate}
          cycleId={cycle.id}
          onClose={() => setNoteDate(null)}
        />
      ) : null}
    </View>
  );
}
